import { DOCUMENT } from "@angular/common";
import { AfterViewInit, Component, OnInit, computed, effect, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Meta, Title } from "@angular/platform-browser";
import { RouterLink } from "@angular/router";
import { SITE_ORIGIN } from "./site.config";
import { setCanonicalLink } from "./seo-utils";
import QRCode from "qrcode";

type Lang = "pt" | "en" | "es";

const SPANISH_COUNTRIES = new Set([
  "ES","MX","AR","CO","CL","PE","VE","EC","GT","CU","BO","DO",
  "HN","PY","SV","NI","CR","PA","UY","GQ","PR",
]);

function normalizePhone(raw: string): string {
  return String(raw ?? "").replace(/\D/g, "");
}

function buildWaMeLink(numberDigits: string, message: string): string {
  const base = `https://wa.me/${numberDigits}`;
  const trimmed = String(message ?? "").trim();
  if (!trimmed) return base;
  return `${base}?text=${encodeURIComponent(trimmed)}`;
}

const TEXT = {
  pt: {
    htmlLang: "pt-BR",
    pageTitle: "Gerador de Link WhatsApp | Criar link wa.me grátis",
    pageDescription: "Crie links de WhatsApp com mensagem personalizada. Ferramenta gratuita para gerar link wa.me rápido e fácil.",
    ogTitle: "Gerador de Link WhatsApp grátis",
    ogDescription: "Crie links de WhatsApp com mensagem personalizada em segundos.",
    appSubtitle: "WhatsMeApp",
    appTitle: "Abra o chat instantaneamente",
    seoH1: "Gerador de Link WhatsApp grátis",
    howItWorks: "Como funciona",
    badge: "Rápido • Simples • Mobile-first",
    hero: "Digite um número do WhatsApp e vá direto para a conversa.",
    heroBody:
      "Informe o código do país, DDD e número. Redirecionamos para o WhatsApp sem salvar contatos.",
    step1Title: "1",
    step1Body: "Digite o número completo",
    step2Title: "2",
    step2Body: "Valide o formato",
    step3Title: "3",
    step3Body: "Abra o chat no WhatsApp",
    cardKicker: "Iniciar um novo chat",
    cardTitle: "Digite o número do WhatsApp",
    fullNumber: "Número",
    countryCode: "Código do país",
    phoneNumber: "DDD + número",
    countryCodePlaceholder: "55",
    phonePlaceholder: "Ex: 11999999999",
    fullNumberHint: "Código do país + DDD + número (ex.: +55 11 99999-9999).",
    presetMessage: "Mensagem opcional",
    presetMessagePlaceholder: "Olá! Quero mais informações.",
    openWhatsApp: "Abrir WhatsApp",
    previewLink: "Link de prévia",
    copy: "Copiar",
    share: "Compartilhar",
    copied: "Link copiado.",
    shareTitle: "Abrir no WhatsApp",
    qrCode: "QR Code",
    qrCodeAlt: "QR Code do link gerado",
    shareOnWhatsApp: "Compartilhar no WhatsApp",
    footerGuidesTitle: "Guias",
    linkComo: "Como criar link",
    linkMensagem: "Link com mensagem",
    linkGerador: "Gerador grátis",
    routeComo: "/como-criar-link-whatsapp",
    routeMensagem: "/link-whatsapp-com-mensagem",
    routeGerador: "/gerador-link-whatsapp-gratis",
    footer: "Copyright © {{year}} WhatsMeApp. Todos os direitos reservados.",
    modalTitle: "Redirecionamento WhatsApp",
    modalClose: "Fechar",
    modalLine1: "Removemos caracteres não numéricos (espaços, traços, parênteses).",
    modalLine2: "O tamanho máximo segue o padrão E.164 (até 15 dígitos).",
    modalLine3a: "O formato do link é",
    modalLine3b: "e opcionalmente adicionamos",
    modalLine4:
      "No celular, abre o app do WhatsApp (se instalado). No desktop, abre o WhatsApp Web.",
    errEmpty: "Digite um número.",
    errShort: "Número muito curto (mín. 8 dígitos).",
    errLong: "Número muito longo (máx. 15 dígitos).",
    errLeadingZero: "Não use 0 no começo. Inclua o código do país.",
    copyFail: "Não foi possível copiar automaticamente. Copie manualmente o link do preview.",
    modalUnsupported: "Seu navegador não suporta esse modal.",
    privacy: "Privacidade",
    privacyTitle: "Privacidade",
    privacyLine1: "Este site não coleta nem armazena números ou mensagens.",
    privacyLine2: "O link é gerado localmente no seu navegador e abre diretamente o WhatsApp (wa.me).",
    privacyLine3: "Não exigimos login e não pedimos permissões além do necessário (copiar/compartilhar).",
  },
  en: {
    htmlLang: "en",
    pageTitle: "WhatsApp Link Generator | Create wa.me link free",
    pageDescription: "Create WhatsApp links with a custom message. Free tool to generate wa.me links fast and easy.",
    ogTitle: "Free WhatsApp link generator",
    ogDescription: "Create WhatsApp links with a custom message in seconds.",
    appSubtitle: "WhatsMeApp",
    appTitle: "Open chat instantly",
    seoH1: "Free WhatsApp link generator",
    howItWorks: "How it works",
    badge: "Fast • Simple • Mobile-first",
    hero: "Type a WhatsApp number and go straight to the conversation.",
    heroBody:
      "Enter the country code, area code, and phone number. We redirect instantly without saving contacts.",
    step1Title: "1",
    step1Body: "Enter the full number",
    step2Title: "2",
    step2Body: "Validate the format",
    step3Title: "3",
    step3Body: "Open WhatsApp chat",
    cardKicker: "Start a new chat",
    cardTitle: "Enter WhatsApp number",
    fullNumber: "Number",
    countryCode: "Country code",
    phoneNumber: "Area code + number",
    countryCodePlaceholder: "55",
    phonePlaceholder: "Ex: 11999999999",
    fullNumberHint: "Country code + area code + number (e.g. +55 11 99999-9999).",
    presetMessage: "Optional preset message",
    presetMessagePlaceholder: "Hi! I want more information.",
    openWhatsApp: "Open WhatsApp",
    previewLink: "Preview link",
    copy: "Copy",
    share: "Share",
    copied: "Link copied.",
    shareTitle: "Open in WhatsApp",
    qrCode: "QR Code",
    qrCodeAlt: "QR Code of the generated link",
    shareOnWhatsApp: "Share on WhatsApp",
    footerGuidesTitle: "Guides",
    linkComo: "How to create a link",
    linkMensagem: "Link with message",
    linkGerador: "Free generator",
    routeComo: "/how-to-create-whatsapp-link",
    routeMensagem: "/whatsapp-link-with-message",
    routeGerador: "/free-whatsapp-link-generator",
    footer: "Copyright © {{year}} WhatsMeApp. All rights reserved.",
    modalTitle: "WhatsApp redirect",
    modalClose: "Close",
    modalLine1: "We remove non-digit characters from the number (spaces, dashes, parentheses).",
    modalLine2: "The max length follows E.164 (up to 15 digits).",
    modalLine3a: "The link format is",
    modalLine3b: "and we optionally add",
    modalLine4:
      "On mobile, the redirect opens the WhatsApp app (if installed). On desktop, it opens WhatsApp Web.",
    errEmpty: "Enter a number.",
    errShort: "Number is too short (min. 8 digits).",
    errLong: "Number is too long (max. 15 digits).",
    errLeadingZero: "Don’t start with 0. Include the country code.",
    copyFail: "Could not copy automatically. Please copy the preview link manually.",
    modalUnsupported: "Your browser doesn't support this modal.",
    privacy: "Privacy",
    privacyTitle: "Privacy",
    privacyLine1: "This site does not collect or store phone numbers or messages.",
    privacyLine2: "The link is generated locally in your browser and opens WhatsApp directly (wa.me).",
    privacyLine3: "No login required and we only request what's needed (copy/share).",
  },
  es: {
    htmlLang: "es",
    pageTitle: "Generador de enlace de WhatsApp | Crear link wa.me gratis",
    pageDescription: "Crea enlaces de WhatsApp con mensaje personalizado. Herramienta gratuita para generar link wa.me rápido y fácil.",
    ogTitle: "Generador de enlace de WhatsApp gratis",
    ogDescription: "Crea enlaces de WhatsApp con mensaje personalizado en segundos.",
    appSubtitle: "WhatsMeApp",
    appTitle: "Abre el chat al instante",
    seoH1: "Generador de enlace de WhatsApp gratis",
    howItWorks: "Cómo funciona",
    badge: "Rápido • Simple • Mobile-first",
    hero: "Escribe un número de WhatsApp y ve directo a la conversación.",
    heroBody:
      "Ingresa el código de país, el código de área y el número. Te redirigimos a WhatsApp sin guardar contactos.",
    step1Title: "1",
    step1Body: "Escribe el número completo",
    step2Title: "2",
    step2Body: "Valida el formato",
    step3Title: "3",
    step3Body: "Abre el chat en WhatsApp",
    cardKicker: "Iniciar un nuevo chat",
    cardTitle: "Escribe el número de WhatsApp",
    fullNumber: "Número",
    countryCode: "Código de país",
    phoneNumber: "Código de área + número",
    countryCodePlaceholder: "52",
    phonePlaceholder: "Ej: 5512345678",
    fullNumberHint: "Código de país + código de área + número (ej.: +52 55 1234-5678).",
    presetMessage: "Mensaje opcional",
    presetMessagePlaceholder: "¡Hola! Quiero más información.",
    openWhatsApp: "Abrir WhatsApp",
    previewLink: "Vista previa del enlace",
    copy: "Copiar",
    share: "Compartir",
    copied: "Enlace copiado.",
    shareTitle: "Abrir en WhatsApp",
    qrCode: "QR Code",
    qrCodeAlt: "Código QR del enlace generado",
    shareOnWhatsApp: "Compartir en WhatsApp",
    footerGuidesTitle: "Guías",
    linkComo: "Cómo crear un enlace",
    linkMensagem: "Enlace con mensaje",
    linkGerador: "Generador gratis",
    routeComo: "/como-crear-enlace-whatsapp",
    routeMensagem: "/enlace-whatsapp-con-mensaje",
    routeGerador: "/generador-enlace-whatsapp-gratis",
    footer: "Copyright © {{year}} WhatsMeApp. Todos los derechos reservados.",
    modalTitle: "Redirección a WhatsApp",
    modalClose: "Cerrar",
    modalLine1: "Eliminamos los caracteres no numéricos (espacios, guiones, paréntesis).",
    modalLine2: "La longitud máxima sigue el estándar E.164 (hasta 15 dígitos).",
    modalLine3a: "El formato del enlace es",
    modalLine3b: "y opcionalmente añadimos",
    modalLine4:
      "En móvil, abre la app de WhatsApp (si está instalada). En escritorio, abre WhatsApp Web.",
    errEmpty: "Escribe un número.",
    errShort: "Número demasiado corto (mín. 8 dígitos).",
    errLong: "Número demasiado largo (máx. 15 dígitos).",
    errLeadingZero: "No empieces con 0. Incluye el código de país.",
    copyFail: "No se pudo copiar automáticamente. Copia el enlace de vista previa manualmente.",
    modalUnsupported: "Tu navegador no soporta este modal.",
    privacy: "Privacidad",
    privacyTitle: "Privacidad",
    privacyLine1: "Este sitio no recopila ni almacena números ni mensajes.",
    privacyLine2: "El enlace se genera localmente en tu navegador y abre WhatsApp directamente (wa.me).",
    privacyLine3: "No requerimos inicio de sesión y solo pedimos lo necesario (copiar/compartir).",
  },
} as const;

function validateE164Digits(
  digits: string,
  t: (k: keyof (typeof TEXT)["en"]) => string
): { ok: boolean; reason: string } {
  if (!digits) return { ok: false, reason: t("errEmpty") };
  if (digits.length < 8) return { ok: false, reason: t("errShort") };
  if (digits.length > 15) return { ok: false, reason: t("errLong") };
  if (digits.startsWith("0")) return { ok: false, reason: t("errLeadingZero") };
  return { ok: true, reason: "" };
}

@Component({
  selector: "app-home",
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: "./home.component.html",
})
export class HomeComponent implements AfterViewInit, OnInit {
  readonly adsenseClient = "ca-pub-4555028756653410";
  readonly adsenseSlot = "4164106323";

  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);

  lang = signal<Lang>(this.loadLang());
  countryCode = signal<string>("55");
  phoneNumber = signal<string>("");
  presetMessage = signal<string>("");
  copyToastVisible = signal<boolean>(false);
  hasInteracted = signal<boolean>(false);
  qrCodeDataUrl = signal<string>("");
  qrCodeVisible = signal<boolean>(false);

  t = (key: keyof (typeof TEXT)["en"]) => TEXT[this.lang()][key];
  year = new Date().getFullYear();
  footerText = () => this.t("footer").replace("{{year}}", String(this.year));

  digits = computed(() => {
    const cc = normalizePhone(this.countryCode());
    const pn = normalizePhone(this.phoneNumber());
    return `${cc}${pn}`;
  });
  validation = computed(() => validateE164Digits(this.digits(), this.t));
  previewLink = computed(() => {
    const v = this.validation();
    if (!v.ok) return "https://wa.me/";
    return buildWaMeLink(this.digits(), this.presetMessage());
  });

  canAct = computed(() => this.validation().ok);
  errorMessage = computed(() => {
    if (!this.hasInteracted()) return "";
    return this.validation().ok ? "" : this.validation().reason;
  });

  constructor() {
    effect(() => {
      const link = this.previewLink();
      if (!this.canAct()) {
        this.qrCodeVisible.set(false);
        this.qrCodeDataUrl.set("");
        return;
      }
      QRCode.toDataURL(link, { margin: 1, width: 200 }).then((dataUrl) => {
        this.qrCodeDataUrl.set(dataUrl);
        this.qrCodeVisible.set(true);
      }).catch(() => { /* ignore */ });
    });

    effect(() => {
      const lang = this.lang();
      const t = (k: keyof (typeof TEXT)["en"]) => TEXT[lang][k];
      this.doc.documentElement.lang = t("htmlLang");
      this.title.setTitle(t("pageTitle"));
      this.meta.updateTag({ name: "description", content: t("pageDescription") });
      this.meta.updateTag({ property: "og:title", content: t("ogTitle") });
      this.meta.updateTag({ property: "og:description", content: t("ogDescription") });
      this.meta.updateTag({ property: "og:url", content: `${SITE_ORIGIN}/` });
    });
  }

  ngOnInit(): void {
    this.meta.updateTag({
      name: "keywords",
      content: "gerar link whatsapp, link whatsapp direto, wa.me, criar link whatsapp, whatsapp link generator, generador enlace whatsapp",
    });
    setCanonicalLink(this.doc, `${SITE_ORIGIN}/`);
    this.detectLangByGeo();
  }

  ngAfterViewInit(): void {
    if (!this.adsenseSlot) return;
    const w = window as Window & { adsbygoogle?: unknown[] };
    w.adsbygoogle = w.adsbygoogle || [];
    window.setTimeout(() => {
      try {
        w.adsbygoogle!.push({});
      } catch {
        /* ignore */
      }
    }, 0);
  }

  private loadLang(): Lang {
    const stored = window.localStorage.getItem("lang");
    if (stored === "pt" || stored === "en" || stored === "es") return stored;
    const nav = (navigator.language || "").toLowerCase();
    if (nav.startsWith("pt")) return "pt";
    if (nav.startsWith("es")) return "es";
    return "en";
  }

  private async detectLangByGeo(): Promise<void> {
    if (window.localStorage.getItem("lang")) return;
    try {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 3000);
      let res: Response;
      try {
        res = await fetch("https://ipapi.co/country/", { signal: controller.signal });
      } finally {
        clearTimeout(timer);
      }
      const country = (await res!.text()).trim().toUpperCase();
      if (country === "BR" || country === "PT") {
        this.setLang("pt");
      } else if (SPANISH_COUNTRIES.has(country)) {
        this.setLang("es");
      } else {
        this.setLang("en");
      }
    } catch {
      /* silently fall back to navigator.language result */
    }
  }

  setLang(lang: Lang): void {
    this.lang.set(lang);
    window.localStorage.setItem("lang", lang);
  }

  toggleLang() {
    const cycle: Record<Lang, Lang> = { pt: "en", en: "es", es: "pt" };
    this.setLang(cycle[this.lang()]);
    this.copyToastVisible.set(false);
  }

  onCountryCodeInput(value: string) {
    const digits = normalizePhone(value);
    this.countryCode.set(digits);
    this.hasInteracted.set(true);
    this.copyToastVisible.set(false);
  }

  onPhoneNumberInput(value: string) {
    const digits = normalizePhone(value);
    this.phoneNumber.set(digits);
    this.hasInteracted.set(true);
    this.copyToastVisible.set(false);
  }

  onPresetMessageInput(value: string) {
    this.presetMessage.set(value);
    this.copyToastVisible.set(false);
  }

  openWhatsApp() {
    this.hasInteracted.set(true);
    const v = this.validation();
    if (!v.ok) return;
    const url = buildWaMeLink(this.digits(), this.presetMessage());
    const win = window.open(url, "_blank", "noopener,noreferrer");

    this.phoneNumber.set("");
    this.presetMessage.set("");
    this.copyToastVisible.set(false);
    this.hasInteracted.set(false);
  }

  async copyLink() {
    if (!this.canAct()) return;
    try {
      await navigator.clipboard.writeText(this.previewLink());
      this.copyToastVisible.set(true);
      window.setTimeout(() => this.copyToastVisible.set(false), 1500);
    } catch {
      window.alert(this.t("copyFail"));
    }
  }

  async shareLink() {
    if (!this.canAct()) return;
    const url = this.previewLink();

    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: this.t("shareTitle"), url });
        return;
      } catch {
        // fall through to copy
      }
    }

    await this.copyLink();
  }

  toggleQrCode() {
    if (!this.canAct()) return;
    this.qrCodeVisible.set(!this.qrCodeVisible());
  }

  shareOnWhatsApp() {
    if (!this.canAct()) return;
    const text = encodeURIComponent(this.previewLink());
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  }

  openHowItWorks() {
    const dialog = document.getElementById("howItWorksDialog") as HTMLDialogElement | null;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else window.alert(this.t("modalUnsupported"));
  }

  closeHowItWorks() {
    const dialog = document.getElementById("howItWorksDialog") as HTMLDialogElement | null;
    dialog?.close();
  }

  openPrivacy() {
    const dialog = document.getElementById("privacyDialog") as HTMLDialogElement | null;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else window.alert(this.t("modalUnsupported"));
  }

  closePrivacy() {
    const dialog = document.getElementById("privacyDialog") as HTMLDialogElement | null;
    dialog?.close();
  }

  onDialogBackdropClick(event: MouseEvent) {
    const dialog = event.currentTarget as HTMLDialogElement;
    const rect = dialog.getBoundingClientRect();
    const inDialog =
      rect.top <= event.clientY &&
      event.clientY <= rect.top + rect.height &&
      rect.left <= event.clientX &&
      event.clientX <= rect.left + rect.width;
    if (!inDialog) dialog.close();
  }
}
